import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Settings2, CheckCircle2, XCircle, RefreshCw, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppSettings() {
  const [c, setC] = useState(null);
  const [tok, setTok] = useState("");
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => setC((await api.get("/whatsapp/connection")).data);
  useEffect(() => { load(); }, []);
  if (!c) return <div className="p-8 text-slate-500">Loading…</div>;

  const save = async () => {
    const payload = { ...c }; if (tok) payload.access_token = tok;
    delete payload.access_token_masked; delete payload.access_token_enc;
    try { const r = await api.put("/whatsapp/connection", payload); setC(r.data); setTok(""); toast.success("Saved"); }
    catch { toast.error("Failed"); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/whatsapp/test");
      if (r.data.ok) toast.success(r.data.message || "Connected"); else toast.error(r.data.message || "Failed");
      load();
    } catch (e) { toast.error("Test failed"); }
    finally { setTesting(false); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const r = await api.post("/whatsapp/templates/sync"); toast.success(`Imported ${r.data.imported} templates from Meta`); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };
  const toggleMode = (live) => setC({ ...c, mode: live ? "live" : "mock" });

  const isLive = c.mode === "live";
  const webhookUrl = `${BACKEND_URL}/api/webhooks/whatsapp`;

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Settings2 className="w-6 h-6"/>WhatsApp Connection</h1>
        <p className="text-sm text-slate-500">Configure your WhatsApp Business Cloud API credentials</p>
      </div>

      {isLive ? (
        <Card className="p-4 border-2 border-emerald-500 rounded-lg bg-emerald-50 text-emerald-900" data-testid="live-mode-banner">
          <div className="font-bold text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4"/>LIVE MODE — Meta WhatsApp Cloud API</div>
          <div className="text-xs mt-1">Real messages will be sent through Meta's official platform. Campaign safety checks are enforced.</div>
        </Card>
      ) : (
        <Card className="mock-banner p-4 border-2 border-amber-400 rounded-lg text-amber-900" data-testid="mock-mode-banner">
          <div className="font-bold text-sm">⚠ MOCK MODE — Simulated WhatsApp Cloud API</div>
          <div className="text-xs mt-1">Messages are simulated only. Switch to Live mode below and enter Meta credentials to send real WhatsApp messages.</div>
        </Card>
      )}

      <Card className="p-6 border-slate-200 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Provider Mode</div>
            <div className="text-sm text-slate-500">{isLive ? "Live — Meta WhatsApp Cloud API" : "Mock — no real messages sent"}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Mock</span>
            <Switch checked={isLive} onCheckedChange={toggleMode} data-testid="live-mode-switch"/>
            <span className="text-sm">Live</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {c.connected ? <><CheckCircle2 className="w-5 h-5 text-emerald-600"/><Badge className="bg-emerald-100 text-emerald-800">Connected</Badge></> :
            <><XCircle className="w-5 h-5 text-rose-500"/><Badge className="bg-rose-100 text-rose-800">Not connected</Badge></>}
          {c.last_test_message && <span className="text-xs text-slate-500">— {c.last_test_message}</span>}
        </div>
      </Card>

      <Card className="p-6 border-slate-200 space-y-4">
        <div>
          <div className="font-semibold text-slate-900">Meta Business credentials</div>
          <div className="text-xs text-slate-500 mt-1">Available in Meta Business Manager → WhatsApp → API Setup.</div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Display name</Label><Input value={c.display_name||""} onChange={(e)=>setC({...c,display_name:e.target.value})} data-testid="wa-display-name"/></div>
          <div><Label>Phone number</Label><Input value={c.phone_number||""} onChange={(e)=>setC({...c,phone_number:e.target.value})} placeholder="+91…" data-testid="wa-phone-number"/></div>
          <div><Label>Phone Number ID</Label><Input value={c.phone_number_id||""} onChange={(e)=>setC({...c,phone_number_id:e.target.value})} data-testid="wa-phone-id"/></div>
          <div><Label>WABA ID</Label><Input value={c.waba_id||""} onChange={(e)=>setC({...c,waba_id:e.target.value})} data-testid="wa-waba-id"/></div>
          <div><Label>Business ID (optional)</Label><Input value={c.business_id||""} onChange={(e)=>setC({...c,business_id:e.target.value})} data-testid="wa-business-id"/></div>
          <div><Label>API version</Label><Input value={c.api_version||"v21.0"} onChange={(e)=>setC({...c,api_version:e.target.value})} data-testid="wa-api-version"/></div>
          <div className="md:col-span-2">
            <Label>Access token {c.access_token_masked && <span className="text-xs text-slate-500 ml-2">(current: {c.access_token_masked})</span>}</Label>
            <Input type="password" value={tok} onChange={(e)=>setTok(e.target.value)} placeholder="Paste new token to replace" data-testid="wa-access-token" autoComplete="new-password"/>
            <div className="text-[10px] text-slate-500 mt-1">Stored encrypted server-side. Never returned to the frontend.</div>
          </div>
          <div className="md:col-span-2"><Label>Webhook verify token</Label><Input value={c.webhook_verify_token||""} onChange={(e)=>setC({...c,webhook_verify_token:e.target.value})} data-testid="wa-webhook-token"/></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-save-button">Save</Button>
          <Button onClick={test} disabled={testing} variant="outline" data-testid="wa-test-button">
            <RefreshCw className={`w-3 h-3 mr-1 ${testing?"animate-spin":""}`}/>Test connection
          </Button>
          {isLive && c.waba_id && (
            <Button onClick={sync} disabled={syncing} variant="outline" data-testid="wa-sync-templates-button">
              <RefreshCw className={`w-3 h-3 mr-1 ${syncing?"animate-spin":""}`}/>Sync templates from Meta
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-6 border-slate-200 space-y-3">
        <div className="font-semibold text-slate-900">Webhook setup</div>
        <div className="text-xs text-slate-500">In Meta App → WhatsApp → Configuration, set this Callback URL and Verify Token:</div>
        <div className="space-y-2 text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div><span className="text-slate-500">Callback URL: </span><span className="text-slate-900">{webhookUrl}</span></div>
          <div><span className="text-slate-500">Verify Token: </span><span className="text-slate-900">{c.webhook_verify_token || "(set one above)"}</span></div>
        </div>
        <div className="text-[10px] text-slate-500">Subscribe to fields: <code>messages</code>. Meta will POST inbound messages and status updates to this URL.</div>
      </Card>
    </div>
  );
}
